/**
 * Scan-level dog profile formatting (MOJ-35).
 *
 * Used by trainer list views (customer overview, plan detail) to show
 * "Name · Rasse · Alter" in one short line. Full profile depth stays on the
 * customer detail page.
 */

export interface DogProfileLike {
  dogName?: string | null;
  dogBreed?: string | null;
  dogBirthDate?: string | Date | null;
}

/** Short German age label from a birth date: "8 Mon.", "2 J.", "2 J. 3 Mon.". */
export function formatDogAge(birthDate: string | Date | null | undefined, now: Date = new Date()): string | null {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  if (isNaN(birth.getTime()) || birth > now) return null;

  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  if (now.getDate() < birth.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  if (years <= 0) return months <= 0 ? '< 1 Mon.' : `${months} Mon.`;
  if (years < 3 && months > 0) return `${years} J. ${months} Mon.`;
  return `${years} J.`;
}

/**
 * One-line dog label: "Mojo · Labrador · 2 J.". Missing parts are skipped.
 * Returns null when nothing is known about the dog.
 */
export function formatDogLabel(dog: DogProfileLike): string | null {
  const parts = [dog.dogName, dog.dogBreed, formatDogAge(dog.dogBirthDate)]
    .map((p) => (typeof p === 'string' ? p.trim() : p))
    .filter((p): p is string => !!p);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** "Tag X von Y" progress label for an assigned plan. */
export function formatDayProgress(completedDays: number, totalDays: number): string {
  if (totalDays <= 0) return 'Keine Tage';
  return `Tag ${Math.min(completedDays, totalDays)} von ${totalDays}`;
}

/**
 * Dog-profile intake vocabulary (MOJ-31).
 *
 * The backend stores these as short string codes (see DogProfileFields in
 * backend/src/schemas/index.ts); this module owns the German labels so the
 * customer profile, signup form and trainer detail view stay consistent.
 */

export interface DogProfileFields {
  dogBreed?: string | null;
  dogBirthDate?: string | null;
  dogGender?: string | null;
  dogNotes?: string | null;
  isFirstDog?: boolean | null;
  livingSituation?: string | null;
  city?: string | null;
  country?: string | null;
  dogOwnership?: string | null;
  trainingSince?: string | null;
  vaccinationStatus?: string | null;
}

export const DOG_GENDER_OPTIONS = [
  { value: 'male', label: 'Rüde' },
  { value: 'female', label: 'Hündin' },
];

export const LIVING_SITUATION_OPTIONS = [
  { value: 'flat', label: 'Wohnung' },
  { value: 'garden', label: 'Haus mit Garten' },
];

export const DOG_OWNERSHIP_OPTIONS = [
  { value: 'first', label: 'Ersthalter' },
  { value: 'previous', label: 'Vorbesitzer / Zweithand' },
];

export const VACCINATION_STATUS_OPTIONS = [
  { value: 'complete', label: 'Vollständig geimpft' },
  { value: 'partial', label: 'Teilweise geimpft' },
  { value: 'none', label: 'Nicht geimpft' },
  { value: 'unknown', label: 'Unbekannt' },
];

export const FIRST_DOG_OPTIONS = [
  { value: 'yes', label: 'Ja, mein erster Hund' },
  { value: 'no', label: 'Nein, ich hatte schon Hunde' },
];

/** "Wie hast du von uns erfahren?" (MOJ-30) — shared by signup, customer profile and trainer views. */
export const ACQUISITION_SOURCE_OPTIONS = [
  { value: 'referral', label: 'Empfehlung' },
  { value: 'web', label: 'Internet' },
  { value: 'other', label: 'Sonstiges' },
];

function labelFor(options: { value: string; label: string }[], value?: string | null): string | null {
  if (!value) return null;
  return options.find((o) => o.value === value)?.label ?? value;
}

export const dogGenderLabel = (v?: string | null) => labelFor(DOG_GENDER_OPTIONS, v);
export const livingSituationLabel = (v?: string | null) => labelFor(LIVING_SITUATION_OPTIONS, v);
export const dogOwnershipLabel = (v?: string | null) => labelFor(DOG_OWNERSHIP_OPTIONS, v);
export const vaccinationStatusLabel = (v?: string | null) => labelFor(VACCINATION_STATUS_OPTIONS, v);
export const firstDogLabel = (v?: boolean | null) => (v === true ? 'Ja' : v === false ? 'Nein' : null);
export const acquisitionSourceLabel = (v?: string | null) => labelFor(ACQUISITION_SOURCE_OPTIONS, v);

/** 'yes' | 'no' | '' form value <-> boolean | null API value */
export const firstDogToForm = (v?: boolean | null): string => (v === true ? 'yes' : v === false ? 'no' : '');
export const firstDogFromForm = (v: string): boolean | null => (v === 'yes' ? true : v === 'no' ? false : null);

/** ISO timestamp -> YYYY-MM-DD for <input type="date"> */
export const toDateInput = (v?: string | null): string => (v ? v.split('T')[0] : '');

/** Format an ISO date for display (de-DE), or null if unset. */
export const formatDateDe = (v?: string | null): string | null => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
